import React, { FC, useState } from "react";
import { Video } from "../Video";
import { ArrowPrev } from "../Icon/icons/ArrowPrev";
import { ArrowNext } from "../Icon/icons/ArrowNext";
import { Image } from "../Image";
import ModalBigPicture from "../GalleryNew/ModalBigPicture";
import ModalAllImages from "../GalleryNew/ModalAllImages";
import { Swiper, SwiperClass, SwiperSlide, useSwiper } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { IGalleryItemProps } from "../GalleryNew";

interface GalleryProps {
  gallery: IGalleryItemProps[];
}

const GalleryItem: FC<IGalleryItemProps> = (item) => {
  return (
    <div className="relative shadow-md rounded-lg bg-black w-full h-30-vh md:h-40-vw md:max-h-50-vh">
      {item.type === "image" && (
        <Image
          src={item.src}
          alt={item.alt || ""}
          className="w-full object-cover overflow-hidden h-full"
          fill={true}
          activeCache={true}
        />
      )}

      {item.type === "video" && (
        <Video src={item.src} thumbnail={item.thumbnail} />
      )}
      {item.caption && (
        <div className="absolute bottom-0 w-full bg-gray-800/80 px-4 py-2 text-white truncate">
          <div className="max-h-12 overflow-hidden text-ellipsis">
            {item.caption}
          </div>
        </div>
      )}
    </div>
  );
};

export const Gallery: FC<GalleryProps> = ({ gallery }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [modalCarrouselVisible, setModalCarrouselVisible] = useState(false);

  const handleNavigateToImage = () => {
    setModalVisible(true);
  };

  const handleCloseImageBigSize = () => {
    setModalCarrouselVisible(false);
  };

  const CarousselActions = () => {
    const swiper = useSwiper();

    const onClickChangeImage = (order: "prev" | "next") => {
      if (currentIndex === 0 && order === "prev") {
        return;
      }
      if (currentIndex >= gallery.length - 1 && order === "next") {
        return;
      }
      setCurrentIndex(swiper.realIndex);
      if (order === "next") {
        swiper.slideNext();
      } else {
        swiper.slidePrev();
      }
    };

    return (
      <div>
        <button
          className={`flex absolute items-center top-1/2 transform -translate-y-1/2 left-0 z-10 px-4 opacity-${
            currentIndex === 0 ? "50" : "100"
          }`}
          onClick={(e) => {
            e.stopPropagation();
            onClickChangeImage("prev");
          }}
          aria-label="Prev"
        >
          <div className="bg-white rounded-full p-2 opacity-70">
            <ArrowPrev className="w-8 h-8 text-black" />
          </div>
        </button>
        <button
          className={`flex absolute items-center top-1/2 transform -translate-y-1/2 right-0 z-10 px-4 opacity-${
            currentIndex === gallery.length - 1 ? "50" : "100"
          }`}
          onClick={(e) => {
            e.stopPropagation();
            onClickChangeImage("next");
          }}
          aria-label="Next"
        >
          <div className="bg-white rounded-full p-2 opacity-70">
            <ArrowNext className="w-8 h-8 text-black" />
          </div>
        </button>
      </div>
    );
  };

  if (gallery.length === 0) {
    return null;
  }

  return (
    <div className="flex relative">
      <div
        onClick={() => handleNavigateToImage()}
        className="w-full h-full bg-black"
      >
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          slidesPerView={1}
          loop={false}
          autoplay={false}
          onSlideChangeTransitionEnd={(e: SwiperClass) => {
            setCurrentIndex(e.realIndex);
          }}
        >
          <CarousselActions />
          {gallery.map((item, index) => (
            <SwiperSlide key={index}>
              <GalleryItem {...item} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <ModalAllImages
        visible={modalVisible}
        gallery={gallery}
        onRequestClose={() => setModalVisible(false)}
      />

      <ModalBigPicture
        items={gallery}
        visible={modalCarrouselVisible}
        currentIndex={currentIndex}
        onRequestClose={handleCloseImageBigSize}
      />
    </div>
  );
};
